// =====================================================
// PAYMENT BUTTON
// Stage 3.2: Payment Processing
// Trigger button for payment actions — dialogs and
// mutations are managed by the parent (EventParticipantsTab)
// =====================================================

import { DollarSign, Check, XCircle, ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// ===================
// TYPES
// ===================

interface PaymentButtonProps {
  paymentStatus: 'PENDING' | 'PARTIAL' | 'PAID' | 'REVERSED';
  onRequestPayment: () => void;
  onRequestReversal: () => void;
  disabled?: boolean;
}

// ===================
// COMPONENT
// ===================

export function PaymentButton({
  paymentStatus,
  onRequestPayment,
  onRequestReversal,
  disabled = false,
}: PaymentButtonProps) {
  const getBadgeVariant = () => {
    switch (paymentStatus) {
      case 'PAID': return 'default';
      case 'PARTIAL': return 'outline';
      case 'REVERSED': return 'destructive';
      default: return 'secondary';
    }
  };

  const getBadgeText = () => {
    switch (paymentStatus) {
      case 'PAID': return 'Pagado';
      case 'PARTIAL': return 'Parcial';
      case 'REVERSED': return 'Revertido';
      default: return 'Pendiente';
    }
  };

  const getBadgeIcon = () => {
    switch (paymentStatus) {
      case 'PAID': return <Check className="mr-1 size-3" />;
      case 'REVERSED': return <XCircle className="mr-1 size-3" />;
      default: return <DollarSign className="mr-1 size-3" />;
    }
  };

  // Unpaid: one obvious action instead of a badge-as-dropdown
  if (paymentStatus === 'PENDING' || paymentStatus === 'REVERSED') {
    return (
      <div className="flex flex-col items-start gap-1">
        <Badge variant={getBadgeVariant()} className={`text-xs ${paymentStatus === 'REVERSED' ? 'bg-red-500' : ''}`}>
          {getBadgeIcon()}
          {getBadgeText()}
        </Badge>
        <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={onRequestPayment} disabled={disabled}>
          <DollarSign className="mr-1 size-3" /> Cobrar
        </Button>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          disabled={disabled}
          className="h-auto justify-start p-0 hover:bg-transparent"
        >
          <Badge
            variant={getBadgeVariant()}
            className={`cursor-pointer text-xs ${
              paymentStatus === 'PAID' ? 'bg-green-500 hover:bg-green-600' : ''
            } ${paymentStatus === 'PARTIAL' ? 'border-amber-500 text-amber-700' : ''}`}
          >
            {getBadgeIcon()}
            {getBadgeText()}
          </Badge>
          <ChevronDown className="ml-1 size-3 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {paymentStatus === 'PARTIAL' && (
          <DropdownMenuItem onClick={onRequestPayment}>
            <DollarSign className="mr-2 size-4" />
            Registrar Pago del Saldo
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={onRequestReversal}>
          <XCircle className="mr-2 size-4" />
          Revertir Pago
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
